import clsx from 'clsx'
import { XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { Tooltip } from '@/components/ui/tooltip/Tooltip'
import type { TypeaheadChipProps } from './types'

/**
 * TypeaheadChip Component
 *
 * A removable chip displaying a selected item within the Typeahead input.
 * Uses tonal neutral styling consistent with Tag component patterns.
 * Long labels are truncated with ellipsis and show full text in a tooltip on hover.
 */
export function TypeaheadChip({ label, onRemove, disabled = false, maxWidth = 'max-w-full' }: TypeaheadChipProps) {
  return (
    <span
      data-ds-component="TypeaheadChip"
      data-testid="typeahead-chip"
      className={clsx(
        'inline-flex items-center gap-1 h-6 pl-2.5 pr-0.5 py-1 rounded',
        maxWidth,
        'bg-brand-neutral-50 border border-brand-neutral-200 text-brand-neutral-800 text-sm',
        disabled && 'opacity-50'
      )}
    >
      <Tooltip title={label} placement="top">
        <span className="truncate">{label}</span>
      </Tooltip>
      {!disabled && (
        <IconButton
          variant="plain"
          size="sm"
          color="neutral"
          icon={<XClose />}
          aria-label={`Remove ${label}`}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          wrapperClassName="shrink-0 h-5 w-5"
        />
      )}
    </span>
  )
}
